import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Mail, ArrowRight, Loader2, CheckCircle, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  
  const { requestMagicLink } = useAuth();

  const handleRequestLink = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await requestMagicLink(email);
      setLinkSent(true);
      toast.success('Magic link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-secondary/30">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <Link to="/landing" className="inline-block">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-4">
              <Sparkles className="w-8 h-8" />
            </div>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Forum Planner</h1>
          <p className="text-muted-foreground mt-2">Your meeting planning companion</p>
        </div>

        <Card className="shadow-xl border-border/50">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Welcome back</CardTitle>
            <CardDescription>
              {linkSent 
                ? 'Check your email for the magic link' 
                : 'Enter your email to receive a magic link'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!linkSent ? (
              <form onSubmit={handleRequestLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="email-input"
                      required
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full gap-2" 
                  disabled={loading}
                  data-testid="request-magic-link-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Send Magic Link
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">
                      Magic link sent!
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-300 mt-1">
                      We've sent a sign-in link to <strong>{email}</strong>
                    </p>
                  </div>
                </div>

                <div className="text-center text-sm text-muted-foreground space-y-2">
                  <p>Click the link in your email to sign in.</p>
                  <p>The link will expire in 15 minutes.</p>
                </div>

                <div className="pt-2 space-y-2">
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => handleRequestLink({ preventDefault: () => {} })}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Resend Magic Link
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    className="w-full gap-2"
                    onClick={() => {
                      setLinkSent(false);
                      setEmail('');
                    }}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Use a different email
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/register" className="text-primary hover:underline">Don't have an account? Sign up</Link>
        </p>
      </div>
    </div>
  );
}
